import { isNumber } from 'lodash';
import type { QueryBuilderRuleValue } from '@ardoq/api-types';
import { NumberInput } from '@ardoq/forms';
import { FlexBox } from '@ardoq/layout';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { ToLabel } from '../atoms';

type NumericRangeInputProps = {
  value?: QueryBuilderRuleValue;
  onValueChange: (value: QueryBuilderRuleValue) => void;
  label: string;
  isQueryBuilderInReadOnlyMode?: boolean;
  inputId?: string;
  isDisabled?: boolean;
  hasWarning?: boolean;
  hideWarningIcon?: boolean;
  displayValueFrom?: string;
  displayValueTo?: string;
  allowDecimals?: boolean;
  i18nLocale?: I18nLocale;
};

const parseToNumber = (value: QueryBuilderRuleValue | undefined) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  return isNumber(value) ? value : parseFloat(value as string);
};

/** this is the minimum width required to display a 1-digit number in one of the number editors. */
const MINIMUM_NUMBER_INPUT_WIDTH = 74;

export const NumericRangeInput = ({
  value,
  onValueChange,
  label,
  isQueryBuilderInReadOnlyMode = false,
  inputId,
  isDisabled,
  hasWarning,
  hideWarningIcon,
  displayValueFrom,
  displayValueTo,
  allowDecimals = true,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: NumericRangeInputProps) => {
  const [from, to] = Array.isArray(value)
    ? value.map(Number)
    : [undefined, undefined];

  const handleValueChange = (newFrom?: number, newTo?: number) => {
    onValueChange([newFrom ?? from ?? NaN, newTo ?? to ?? NaN]);
  };

  return (
    <>
      <FlexBox flex={1} minWidth={`${MINIMUM_NUMBER_INPUT_WIDTH}px`}>
        <NumberInput
          name={`${label} from`}
          aria-label={t(
            i18nLocale,
            'queryBuilder:ariaLabel.ruleValueNumberFrom',
            'Rule value number from'
          )}
          isReadOnly={isQueryBuilderInReadOnlyMode}
          allowBlank
          allowDecimals={allowDecimals}
          value={parseToNumber(from)}
          displayValue={displayValueFrom}
          onValueChange={value => handleValueChange(value, parseToNumber(to))}
          dataTestId="number-input-from"
          inputId={inputId}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          hideWarningIcon={hideWarningIcon}
        />
      </FlexBox>
      <ToLabel>{t(i18nLocale, 'queryBuilder:rule.rangeTo', 'to')}</ToLabel>
      <FlexBox flex={1} minWidth={`${MINIMUM_NUMBER_INPUT_WIDTH}px`}>
        <NumberInput
          name={`${label} to`}
          aria-label={t(
            i18nLocale,
            'queryBuilder:ariaLabel.ruleValueNumberTo',
            'Rule value number to'
          )}
          isReadOnly={isQueryBuilderInReadOnlyMode}
          allowBlank
          allowDecimals={allowDecimals}
          value={parseToNumber(to)}
          displayValue={displayValueTo}
          onValueChange={value => handleValueChange(parseToNumber(from), value)}
          dataTestId="number-input-to"
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          hideWarningIcon={hideWarningIcon}
        />
      </FlexBox>
    </>
  );
};
